import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { WebhookEventType, WebhookStatus, ListWebhooksParams } from '@/types/webhook';

interface WebhookFiltersProps {
  /** Current filter values */
  filters: ListWebhooksParams;
  /** Callback when filters change */
  onFiltersChange: (filters: ListWebhooksParams) => void;
}

const EVENT_TYPE_VALUES: WebhookEventType[] = [
  'transaction.charged',
  'transaction.failed',
  'transaction.expired',
  'subscription.renewed',
  'subscription.closed',
];

const STATUS_VALUES: WebhookStatus[] = ['received', 'processed', 'failed'];

/**
 * Filter controls for the webhook list.
 */
export function WebhookFilters({ filters, onFiltersChange }: WebhookFiltersProps) {
  const { t } = useTranslation('webhooks');
  const [transactionId, setTransactionId] = React.useState(filters.transactionId || '');

  const handleEventTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      eventType: value === 'all' ? undefined : (value as WebhookEventType),
      page: 1, // Reset to first page on filter change
    });
  };

  const handleStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      status: value === 'all' ? undefined : (value as WebhookStatus),
      page: 1,
    });
  };

  const handleTransactionIdSearch = () => {
    onFiltersChange({
      ...filters,
      transactionId: transactionId || undefined,
      page: 1,
    });
  };

  const handleTransactionIdKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTransactionIdSearch();
    }
  };

  const handleClearFilters = () => {
    setTransactionId('');
    onFiltersChange({
      page: 1,
      limit: filters.limit,
    });
  };

  const hasActiveFilters = filters.eventType || filters.status || filters.transactionId;

  return (
    <div className="flex flex-wrap items-center gap-4">
      {/* Event Type Filter */}
      <div className="flex items-center gap-2">
        <label htmlFor="event-type-filter" className="text-sm font-medium whitespace-nowrap">
          {t('filter.eventType')}
        </label>
        <Select
          value={filters.eventType || 'all'}
          onValueChange={handleEventTypeChange}
        >
          <SelectTrigger id="event-type-filter" className="w-[200px]">
            <SelectValue placeholder={t('common:label.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common:label.all')}</SelectItem>
            {EVENT_TYPE_VALUES.map((eventType) => (
              <SelectItem key={eventType} value={eventType}>
                {t(`eventTypes.${eventType}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Status Filter */}
      <div className="flex items-center gap-2">
        <label htmlFor="status-filter" className="text-sm font-medium whitespace-nowrap">
          {t('filter.status')}
        </label>
        <Select
          value={filters.status || 'all'}
          onValueChange={handleStatusChange}
        >
          <SelectTrigger id="status-filter" className="w-[150px]">
            <SelectValue placeholder={t('common:label.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common:label.all')}</SelectItem>
            {STATUS_VALUES.map((status) => (
              <SelectItem key={status} value={status}>
                {t(`common:status.${status}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Transaction ID Search */}
      <div className="flex items-center gap-2">
        <label htmlFor="transaction-id-filter" className="text-sm font-medium whitespace-nowrap">
          {t('filter.transactionId')}
        </label>
        <div className="flex gap-1">
          <Input
            id="transaction-id-filter"
            type="text"
            placeholder={t('filter.placeholder')}
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            onKeyDown={handleTransactionIdKeyDown}
            className="w-[200px]"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={handleTransactionIdSearch}
            aria-label={t('common:button.search')}
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearFilters}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="mr-1 h-4 w-4" />
          {t('common:button.resetFilters')}
        </Button>
      )}
    </div>
  );
}
